// ─────────────────────────────────────────────────────────────────────────────
// AdminTransactions — Razorpay-style transactions console for every payment
// and refund that flows through HCEN.
//
// 2026-05 OVERHAUL (this revision):
//   • Refund Mode (Auto / Semi-Auto / Manual) MOVED OUT of this page into
//     Platform Settings — it's a global platform policy, not a per-txn action.
//   • "All" view now MERGES each payment with its refund(s) into a single row
//     showing the current status (Captured / Refunded / Partially refunded /
//     Failed / Pending). Fixes the "1 transaction shown as 2 rows" bug.
//   • Payments tab    = every payment, with its current effective status.
//   • Refunds tab     = every refund as a first-class row (refund ID, ARN,
//     amount, status, parent payment) — like Razorpay's Refunds list.
//   • Filter bar redesigned: Date Range pill (From → To), Status, Search,
//     Refresh — matches the Razorpay Payments page layout.
//   • Drawer shows Razorpay-style timeline + the linked refund(s) inline,
//     with copyable Payment ID / Order ID / Refund ID / ARN.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  MdRefresh, MdClose, MdSearch, MdPayments, MdAssignmentReturn,
  MdCheckCircle, MdError, MdPending, MdAccessTime, MdContentCopy,
  MdStickyNote2, MdReceiptLong, MdAccountBalanceWallet, MdCheck, MdSwapHoriz,
  MdBuild, MdDateRange,
} from 'react-icons/md';
import { adminAPI } from '../../services/api';
import {
  isSyntheticBackfill, pendingLinkedRefund,
  refundActionTarget, settleRefundRow,
  displayTxnStatus, hasRazorpayGatewayId,
} from './adminRefundUtils';
import { patientFeeBreakdownFromGross } from '../../utils/patientPlatformFee';

// ── Styling tokens (match AdminPanel's dark palette) ────────────────────────
const BG        = '#0b1220';
const CARD      = '#111827';
const BORDER    = 'rgba(148,163,184,0.22)';
const SUBTLE    = '#cbd5e1';
const MUTED     = '#94a3b8';
const TEXT      = '#f8fafc';
const ACCENT    = '#f59e0b';
const GREEN     = '#10b981';
const RED       = '#ef4444';
const BLUE      = '#60a5fa';
const PURPLE    = '#a78bfa';

// Razorpay-style status labels.
//   • "Captured" = payment success                  (Razorpay parlance)
//   • "Refunded" = payment fully refunded
//   • "Partially refunded" = payment with a partial refund
//   • Refund-row statuses keep their canonical names (Requested / Initiated /
//     Processing / Credited / Failed) so admins know exactly what state the
//     gateway is in.
const STATUS_LABEL = {
  // payment-row statuses
  pending:'Pending', success:'Captured', failed:'Failed',
  refunded:'Refunded', partially_refunded:'Partially refunded',
  refund_pending:'Refund pending',
  // refund-row statuses
  requested:'Requested', initiated:'Initiated',
  processed:'Refund processed', credited:'Credited',
  historical:'Historical reconciled',
  manual_credited:'Manually credited',
};
const STATUS_COLOR = {
  success:GREEN, credited:GREEN, manual_credited:ACCENT,
  refunded:BLUE, partially_refunded:BLUE, refund_pending:ACCENT,
  pending:ACCENT, requested:ACCENT, initiated:ACCENT, processed:BLUE,
  failed:RED,
  historical:'#94a3b8',
};

// Refund-policy machine keys → human-readable label.
// Hospital/admin cancels are always 100% (includes platform fee + GST).
// Patient cancels are tiered on the consultation fee only:
//   ≥5h before slot → 100%, ≥4h → 75%, ≥3h → 50%, ≥2h → 25%, else 0%.
// `patient_partial` is the legacy single-tier 50% key — kept so historical
// rows written before May 2026 still render with a sensible label.
const POLICY_LABEL = {
  hospital_full:   'Hospital cancelled · 100% (incl. platform fee)',
  admin:           'Admin override',
  patient_100:     'Patient cancelled · 100% (≥5h before slot)',
  patient_75:      'Patient cancelled · 75% (≥4h before slot)',
  patient_50:      'Patient cancelled · 50% (≥3h before slot)',
  patient_25:      'Patient cancelled · 25% (≥2h before slot)',
  patient_none:    'Patient cancelled · No refund (<2h)',
  patient_partial: 'Patient cancelled · 50% (legacy tier)',
  full:            'Hospital cancelled · 100%',
  half:            'Patient cancelled · 50% (legacy)',
  none:            'No refund (legacy)',
  other:           'Other',
};

const fmtRS = (n) => `₹${(Number(n)||0).toLocaleString('en-IN')}`;
// Money with exact paise — used in the breakdown so ₹3.60 never shows as ₹3.6.
const fmtMoney = (n) => `₹${(Number(n)||0).toLocaleString('en-IN',{minimumFractionDigits:2, maximumFractionDigits:2})}`;

// Tiered platform fee (mirrors backend): < ₹700 → 5% + GST; ≥ ₹700 → 4% + GST.
// Used only to reconstruct legacy rows with no stored snapshot.
const GST_PCT = 18;

function amountBreakdown(txn) {
  if (!txn) return null;
  const total = Number(txn.amount || 0);
  const fee = txn.breakdownPatientPlatformFee;
  const gst = txn.breakdownPatientGst;
  const base = txn.breakdownOpFee;
  const discount = Number(txn.breakdownDiscount || 0);
  if (fee != null || gst != null || base != null) {
    const f = Number(fee || 0), g = Number(gst || 0);
    const b = base != null ? Number(base) : Math.max(0, total + discount - f - g);
    const snapPct = txn.patientPlatformFeePct ?? txn.breakdownPatientPlatformFeePct;
    return { base:b, fee:f, gst:g, discount, total, feePct: snapPct ?? null, gstPct:GST_PCT, derived:false };
  }
  if (total <= 0) return null;
  const derived = patientFeeBreakdownFromGross(total, discount);
  return { base:derived.base, fee:derived.fee, gst:derived.gst, discount:derived.discount, total, feePct:derived.pct, gstPct:GST_PCT, derived:true };
}
function parseWhen(v) {
  if (v == null || v === '') return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}
function resolveWhen(row) {
  return parseWhen(row?.createdAt)
    || parseWhen(row?.updatedAt)
    || parseWhen(row?.creditedAt)
    || parseWhen(row?.paidAt);
}
const fmtDT = (s) => {
  const d = parseWhen(s);
  return d ? d.toLocaleString('en-IN', {
    day:'2-digit', month:'short', year:'numeric',
    hour:'2-digit', minute:'2-digit', hour12:true,
  }) : '—';
};
const fmtShortDT = (s) => {
  const d = parseWhen(s);
  return d ? d.toLocaleString('en-IN', {
    day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit', hour12:true,
  }) : '—';
};
const fmtRowWhen = (row) => fmtShortDT(resolveWhen(row));

// Razorpay-style short payment-method label for the Customer column subtext.
// Mirrors the "UPI / Credit Card / Netbanking" line shown under the phone
// number in the Razorpay Payments table.
function paymentMethodLabel(row) {
  const m = String(row?.paymentMethod || '').toLowerCase();
  if (!m) return '';
  if (m === 'upi')        return 'UPI';
  if (m === 'card')       return row?.cardType === 'debit' ? 'Debit Card' : 'Credit Card';
  if (m === 'netbanking') return 'Netbanking';
  if (m === 'wallet')     return 'Wallet';
  if (m === 'emi')        return 'EMI';
  return m.charAt(0).toUpperCase() + m.slice(1);
}

// ???????????????????????????????????????????????????????????????????????????
// Main component
// ???????????????????????????????????????????????????????????????????????????
export default function AdminTransactions({ adminEmail, toast }) {
  const [rows, setRows]       = useState([]);
  const [totals, setTotals]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [actionBusy, setActionBusy] = useState(false);

  const [toastMsg, setToastMsg] = useState(null);
  const notify = useCallback((msg, isError = false) => {
    if (toast) toast(msg, isError);
    else {
      setToastMsg({ msg, isError });
      window.clearTimeout(notify._t);
      notify._t = window.setTimeout(() => setToastMsg(null), 5000);
    }
  }, [toast]);

  // Sub-tab: 'all' | 'payments' | 'refunds' | 'razorpay' (live gateway rows only)
  const [view, setView] = useState('all');
  const [syncing, setSyncing] = useState(false);

  // Filters (manual)
  const [filterStatus, setFilterStatus] = useState('');
  const [from,         setFrom]         = useState('');
  const [to,           setTo]           = useState('');
  const [search,       setSearch]       = useState('');
  const [dateOpen,     setDateOpen]     = useState(false);

  // Side drawer
  const [selected, setSelected] = useState(null);

  // One-shot historical backfill state
  const [backfilling, setBackfilling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    // ── Key change: for the "All" view we fetch BOTH payments and refunds in
    // one request (no type filter) and merge them client-side so a refunded
    // booking shows ONE row, not two. The Payments and Refunds sub-tabs still
    // hit their narrow type filters so DynamoDB does less work.
    const typeFromView = view === 'payments' ? 'payment' : view === 'refunds' ? 'refund' : '';
    const { data, error } = await adminAPI.listTransactions({
      type: typeFromView, status: filterStatus,
      from, to, q: search, limit: 1000,
    });
    if (error) { setError(error); setLoading(false); return; }
    setRows(data?.transactions || []);
    setTotals(data?.totals || null);
    setLoading(false);
  }, [view, filterStatus, from, to, search]);

  useEffect(() => { load(); }, [load]);

  // ── Refund-mode filter (only used by the Refunds sub-tab) ─────────────────
  // 'all' shows every refund; 'auto' / 'semi-auto' / 'manual' filter by the
  // mode the platform was in WHEN the refund row was written. Inferred
  // client-side from cancelledBy + status because the legacy backend doesn't
  // stamp the mode on the row yet (see modeOfRefund() below).
  const [refundModeFilter, setRefundModeFilter] = useState('all');

  // ── Display rows ─────────────────────────────────────────────────────────
  // Razorpay/Cashfree-style: in the "All" view a payment and its refund(s)
  // are ONE ledger entry — the payment row's status flips to "Refunded" (or
  // "Partially refunded") instead of showing as two separate transactions.
  // The Payments / Refunds sub-tabs still list each row individually so
  // admins can drill into the refund's own ARN / status / timeline.
  const displayRows = useMemo(() => {
    // Refund-mode filter (Refunds tab only)
    let base = rows;
    if (view === 'refunds' && refundModeFilter !== 'all') {
      base = rows.filter(r => modeOfRefund(r) === refundModeFilter);
    }
    if (view === 'razorpay') {
      return base.filter(r => hasRazorpayGatewayId(r));
    }
    if (view !== 'all') return base;

    // Merge refunds into their parent payments for the All view.
    const payments = [];
    const refundsByParent = {};        // parentTransactionId -> [refund rows]
    const refundsByBooking = {};       // bookingId -> [refund rows]   (fallback)
    const orphanRefunds = [];          // refunds with no resolvable parent payment

    base.forEach(r => {
      if (r.type === 'payment') {
        payments.push(r);
      } else if (r.type === 'refund') {
        if (r.parentTransactionId) {
          (refundsByParent[r.parentTransactionId] ||= []).push(r);
        } else if (r.bookingId) {
          (refundsByBooking[r.bookingId] ||= []).push(r);
        } else {
          orphanRefunds.push(r);
        }
      }
    });

    const merged = payments.map(p => {
      const linked = [
        ...(refundsByParent[p.transactionId] || []),
        ...(refundsByBooking[p.bookingId]    || []),
      ];
      if (!linked.length) return p;

      // Total credited refund amount (only 'credited' counts as money out the door;
      // initiated/processed are in-flight; requested/failed don't reduce revenue).
      const creditedTotal = linked
        .filter(x => !isSyntheticBackfill(x) && ['credited', 'processed', 'initiated'].includes(x.status))
        .reduce((s, x) => s + Number(x.amount || 0), 0);
      const anyCredited   = linked.some(x => x.status === 'credited' && !isSyntheticBackfill(x));
      const anyPending    = linked.some(x => ['requested', 'initiated', 'processed'].includes(x.status));
      const anyFailed     = linked.some(x => x.status === 'failed');
      const paidAmount    = Number(p.amount || 0);

      // Decide effective status (Razorpay parlance).
      //   credited == paid → "refunded"
      //   credited <  paid → "partially_refunded"
      //   in-flight only   → keep payment as "success" but tag refund_pending
      //   only failed      → keep payment as "success" (the refund attempt failed)
      let effectiveStatus = p.status;
      if (anyCredited && creditedTotal >= paidAmount) effectiveStatus = 'refunded';
      else if (anyCredited)                            effectiveStatus = 'partially_refunded';
      // Pending refund only — surface clearly without mis-labelling as REFUNDED.
      if (anyPending && !anyCredited) effectiveStatus = 'refund_pending';

      // Sort newest refund first so the merged row reflects the latest action.
      const sorted = [...linked].sort((a, b) =>
        String(b.createdAt || '').localeCompare(String(a.createdAt || ''))
      );
      const latest = sorted[0];

      return {
        ...p,
        status: effectiveStatus,
        __refundLinked:  sorted,
        __refundCredited: creditedTotal,
        __refundPending:  anyPending,
        __refundFailed:   anyFailed,
        // Show the most-recent refund's timestamp as the row's "Created" so
        // refunded bookings float to the top alongside fresh payments.
        updatedAt: latest?.updatedAt || latest?.createdAt || p.updatedAt,
      };
    });

    // Orphan refunds (no payment row in the page) still need to show or admins
    // would lose them. Show them as standalone refund rows.
    const allMerged = [...merged, ...orphanRefunds];

    // Sort newest-first by createdAt (matches what the backend already does).
    allMerged.sort((a, b) =>
      String(b.createdAt || '').localeCompare(String(a.createdAt || ''))
    );
    return allMerged;
  }, [rows, view, refundModeFilter]);

  // Index payments by transactionId so a refund row can show its parent
  // payment ID inline (Razorpay-style cross-reference). The /admin/transactions
  // endpoint already includes both types in one response when type filter is
  // empty, but the Refunds tab fetches refunds only — so this is a best-effort
  // lookup against whatever's in the current page.
  const openDetail = async (txn) => {
    const linked = txn.__refundLinked;
    setSelected({ ...txn, __loading:true });
    const { data, error } = await adminAPI.getTransaction(txn.transactionId);
    if (error) { setSelected({ ...txn, __loading:false, __refundLinked: linked }); return; }
    if (data) setSelected({ ...data, __loading:false, __refundLinked: linked || data.__refundLinked });
  };

  const runApproveRefund = useCallback(async (r) => {
    const target = refundActionTarget(r);
    if (!target?.transactionId) {
      notify('No pending refund row found — open the payment or use Refunds tab', true);
      return;
    }
    const manual = isSyntheticBackfill(target);
    const confirmMsg = manual
      ? `Mark refund of ${fmtRS(target.amount)} as manually credited?\n\nNo Razorpay API — historical/backfill or offline payout.`
      : `Approve refund of ${fmtRS(target.amount)} for ${target.userName || target.userPhone}?\n\nThis calls Razorpay to credit the customer. This cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;
    setActionBusy(true);
    const { error } = await settleRefundRow(target, { adminEmail, adminAPI });
    setActionBusy(false);
    if (error) { notify(`Could not approve refund: ${error}`, true); return; }
    notify(manual ? '✅ Marked as manually credited' : '✅ Refund submitted to Razorpay');
    load();
  }, [adminEmail, load, notify]);
  const closeDetail = () => setSelected(null);

  // Trigger one-shot historical backfill (creates synthetic payment+refund rows
  // for every old booking). Idempotent on the backend so re-clicking is safe.
  const runBackfill = async () => {
    const ok = window.confirm(
      'Backfill historical bookings into Transactions?\n\n' +
      'This scans the Bookings table and creates synthetic payment + refund rows ' +
      'for every confirmed/cancelled booking that pre-dates the Transactions table.\n\n' +
      'Safe to run multiple times — duplicates are skipped automatically.'
    );
    if (!ok) return;
    setBackfilling(true);
    const { data, error } = await adminAPI.backfillTransactions();
    setBackfilling(false);
    if (error) {
      notify('Backfill failed: ' + error, true);
      return;
    }
    notify(
      `✅ Backfill complete — ${data?.paymentsCreated ?? 0} payments, ${data?.refundsCreated ?? 0} refunds created`
    );
    load();
  };

  const syncWithRazorpay = async (txn) => {
    if (!txn?.transactionId) return;
    setSyncing(true);
    const { data, error } = await adminAPI.syncRazorpay(txn.transactionId);
    setSyncing(false);
    if (error) { notify('Razorpay sync failed: ' + error, true); return; }
    if (data?.reason === 'historical_backfill') {
      notify('Historical row — not on Razorpay. Use manual credit only if paid offline.', true);
      return;
    }
    if (data?.synced) {
      notify(`✅ Synced with Razorpay — ${data.razorpayStatus || data.status}`);
      load();
      if (selected?.transactionId === txn.transactionId) openDetail(txn);
    } else {
      notify(data?.message || 'Could not sync with Razorpay', true);
    }
  };

  const onAction = async (action, payload = {}) => {
    if (!selected || actionBusy) return;
    if (action === 'initiate' || action === 'mark-manual') {
      const refundRow = selected.type === 'refund'
        ? selected
        : { ...(pendingLinkedRefund(selected) || {}), transactionId: payload.refundId };
      if (!refundRow?.transactionId) {
        notify('No pending refund row found for this payment', true);
        return;
      }
      if (action === 'mark-manual' || isSyntheticBackfill(refundRow)) {
        if (!window.confirm('Mark this refund as manually credited (no Razorpay API)?')) return;
        setActionBusy(true);
        const { error } = await adminAPI.markRefundCredited(refundRow.transactionId, {
          adminEmail,
          note: payload.note || 'Marked credited manually by admin',
        });
        setActionBusy(false);
        if (error) { notify('Could not mark credited: ' + error, true); return; }
        notify('✅ Marked as manually credited');
      } else {
        await runApproveRefund(refundRow);
      }
    } else if (action === 'issue-refund') {
      if (isSyntheticBackfill(selected)) {
        const pending = pendingLinkedRefund(selected);
        if (pending) {
          await runApproveRefund(pending);
          return;
        }
        notify(
          'Historical reconciled payment — no Razorpay charge. Use Refunds → Mark manual credit if money is owed.',
          true
        );
        return;
      }
      if (!selected.bookingId) {
        notify('This payment has no booking ID — cannot create a refund', true);
        return;
      }
      if (!window.confirm(
        `Issue Razorpay refund of ${fmtRS(selected.amount)} for booking ${selected.bookingId}?`
      )) return;
      setActionBusy(true);
      const { error } = await adminAPI.issueRefundFromPayment(selected.transactionId, {
        bookingId: selected.bookingId,
        adminEmail,
        reason: 'Admin issue refund from Transactions',
      });
      setActionBusy(false);
      if (error) { notify('Refund failed: ' + error, true); load(); return; }
      notify('✅ Refund sent to Razorpay');
    } else if (action === 'note') {
      const note = window.prompt('Admin note (visible in timeline):');
      if (!note || !note.trim()) return;
      setActionBusy(true);
      const { error } = await adminAPI.addTransactionNote(selected.transactionId, note.trim(), adminEmail);
      setActionBusy(false);
      if (error) { notify('Could not save note: ' + error, true); return; }
      notify('Note saved');
    }
    const linked = selected.__refundLinked;
    const { data } = await adminAPI.getTransaction(selected.transactionId);
    if (data) setSelected({ ...data, __loading:false, __refundLinked: linked });
    load();
  };

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <div style={{padding:20}}>
      {toastMsg && (
        <div style={{
          position:'fixed', bottom:24, right:24, zIndex:9999, maxWidth:420,
          padding:'12px 16px', borderRadius:10, fontSize:13, fontWeight:600,
          color:'var(--text-primary)', boxShadow:'0 8px 32px rgba(0,0,0,0.45)',
          background: toastMsg.isError ? 'rgba(127,29,29,0.95)' : 'rgba(15,23,42,0.95)',
          border: `1px solid ${toastMsg.isError ? 'rgba(248,113,113,0.5)' : 'rgba(34,197,94,0.4)'}`,
        }} role="status">{toastMsg.msg}</div>
      )}
      {/* Page header — Razorpay-style title + utility buttons */}
      <div style={{display:'flex', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:10}}>
        <div style={{flex:1}}>
          <div style={{color:TEXT, fontSize:22, fontWeight:800}}>Transactions</div>
          <div style={{color:MUTED, fontSize:12, marginTop:2}}>
            Every payment and refund processed via Razorpay. Refund policy is configured in <strong style={{color:TEXT}}>Platform Settings</strong>.
          </div>
        </div>
        <button onClick={load} style={BTN_GHOST}><MdRefresh/> Refresh</button>
        {view === 'razorpay' && (
          <button
            onClick={async () => {
              const live = displayRows.filter(hasRazorpayGatewayId).slice(0, 25);
              if (!live.length) { notify('No Razorpay-linked rows to sync', true); return; }
              setSyncing(true);
              let ok = 0;
              for (const r of live) {
                const { data } = await adminAPI.syncRazorpay(r.transactionId);
                if (data?.synced) ok++;
              }
              setSyncing(false);
              notify(`✅ Synced ${ok}/${live.length} rows with Razorpay`);
              load();
            }}
            disabled={syncing}
            style={{...BTN_GHOST, opacity: syncing ? 0.6 : 1}}>
            <MdRefresh/> {syncing ? 'Syncing…' : 'Sync all with Razorpay'}
          </button>
        )}
        <button
          onClick={runBackfill}
          disabled={backfilling}
          title='Backfill old bookings into the Transactions table (safe to re-run)'
          style={{...BTN_GHOST, opacity: backfilling ? 0.5 : 1, cursor: backfilling ? 'wait' : 'pointer'}}>
          <MdBuild/> {backfilling ? 'Backfilling…' : 'Backfill old bookings'}
        </button>
      </div>

      {/* Top stat cards — context-aware */}
      <StatGrid totals={totals} view={view} rowCount={displayRows.length}/>

      {/* Sub-tabs: All / Payments / Refunds */}
      <SubTabs
        view={view}
        setView={setView}
        totals={totals}
        allCount={view === 'all' ? displayRows.length : undefined}
      />

      {view === 'razorpay' && (
        <div style={{
          marginTop:12, padding:'10px 14px', borderRadius:10, fontSize:12.5, lineHeight:1.55,
          background:'rgba(96,165,250,0.08)', border:'1px solid rgba(96,165,250,0.35)', color:SUBTLE,
        }}>
          <strong style={{color:BLUE}}>Razorpay live ledger</strong> — only rows with real{' '}
          <code style={{color:TEXT}}>pay_…</code> / <code style={{color:TEXT}}>rfnd_…</code> IDs.
          Status is verified against the Razorpay API (Captured, Refund processed, etc.).
          BACKFILL rows are excluded — they are historical reconciliations, not gateway charges.
        </div>
      )}

      {/* Filters — Razorpay-style: Date Range pill, Status, Search */}
      <div style={{
        background:CARD, border:`1px solid ${BORDER}`, borderRadius:12,
        padding:12, marginTop:12, display:'flex', flexWrap:'wrap', gap:10, alignItems:'center',
        position:'relative',
      }}>
        {/* Date Range pill */}
        <DateRangePill
          from={from} to={to}
          onChange={(f, t) => { setFrom(f); setTo(t); }}
          open={dateOpen} setOpen={setDateOpen}
        />

        {/* Status filter */}
        <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} style={SELECT}>
          <option value=''>All statuses</option>
          {view === 'refunds' ? (
            <>
              <option value='requested'>Requested</option>
              <option value='initiated'>Initiated</option>
              <option value='processed'>Refund processed</option>
              <option value='credited'>Credited (gateway)</option>
              <option value='historical'>Historical reconciled</option>
              <option value='failed'>Failed</option>
            </>
          ) : (
            <>
              <option value='success'>Captured (paid)</option>
              <option value='pending'>Pending</option>
              <option value='failed'>Failed</option>
            </>
          )}
        </select>

        {/* Search */}
        <div style={{position:'relative', flex:'1 1 240px', minWidth:200}}>
          <MdSearch style={{position:'absolute', left:10, top:9, color:MUTED}}/>
          <input
            placeholder='Search Payment ID, Refund ID, phone, booking, order, UTR, name…'
            value={search}
            onChange={e=>setSearch(e.target.value)}
            style={{...INPUT, paddingLeft:32, width:'100%'}}
          />
        </div>

        {/* Active filter chips */}
        {(filterStatus || from || to || search) && (
          <button
            onClick={() => { setFilterStatus(''); setFrom(''); setTo(''); setSearch(''); }}
            style={{
              ...BTN_GHOST, padding:'6px 10px', fontSize:11, color:RED, borderColor:`${RED}55`,
            }}>
            ✕ Clear
          </button>
        )}
      </div>

      {/* ── Refund-mode pills (Refunds tab only) ──────────────────────────
          Lets the admin slice the refunds list by which mode handled it
          (Auto / Semi-Auto / Manual). Mode is inferred per-row from the
          status + cancelledBy. Counts are computed off the rows currently
          loaded so the pill shows what a click will reveal. */}
      {view === 'refunds' && (
        <div style={{
          marginTop:10, display:'flex', gap:8, flexWrap:'wrap', alignItems:'center',
          background:CARD, border:`1px solid ${BORDER}`, borderRadius:12, padding:10,
        }}>
          <span style={{color:SUBTLE, fontSize:11, fontWeight:800, letterSpacing:1, marginRight:4}}>
            REFUND MODE
          </span>
          {[
            { id:'all',       label:'All',       color:TEXT  },
            { id:'auto',      label:'Auto',      color:GREEN },
            { id:'semi-auto', label:'Semi-Auto', color:ACCENT },
            { id:'manual',    label:'Manual',    color:BLUE  },
          ].map(p => {
            const active = refundModeFilter === p.id;
            const count = p.id === 'all'
              ? rows.length
              : rows.filter(r => modeOfRefund(r) === p.id).length;
            return (
              <button key={p.id} onClick={() => setRefundModeFilter(p.id)} style={{
                padding:'6px 12px', borderRadius:999, fontSize:12, fontWeight:800,
                border:`1px solid ${active ? p.color : BORDER}`,
                background: active ? `${p.color}22` : 'transparent',
                color: active ? p.color : TEXT,
                cursor:'pointer', fontFamily:'inherit',
                display:'inline-flex', alignItems:'center', gap:6,
              }}>
                {p.label}
                <span style={{
                  background: active ? `${p.color}33` : 'rgba(148,163,184,0.15)',
                  color: active ? p.color : SUBTLE,
                  padding:'1px 7px', borderRadius:999, fontSize:10.5, fontWeight:900,
                }}>{count}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Table */}
      <div style={{
        background:CARD, border:`1px solid ${BORDER}`, borderRadius:12,
        marginTop:14, overflow:'hidden',
      }}>
        {loading ? (
          <div style={{padding:40, textAlign:'center', color:SUBTLE}}>Loading transactions…</div>
        ) : error ? (
          <div style={{padding:40, textAlign:'center', color:RED}}>{error}</div>
        ) : displayRows.length === 0 ? (
          <div style={{padding:40, textAlign:'center', color:SUBTLE}}>
            <MdPayments size={40} style={{opacity:0.4}}/>
            <div style={{marginTop:8}}>No {view === 'all' ? 'transactions' : view} match the filters.</div>
          </div>
        ) : (
          <div style={{maxHeight:'62vh', overflow:'auto'}}>
            <table style={{width:'100%', borderCollapse:'collapse', fontSize:14, color:TEXT}}>
              <thead style={{position:'sticky', top:0, background:'#0f172a', zIndex:1, boxShadow:'0 1px 0 rgba(148,163,184,0.18)'}}>
                <tr>{headersFor(view).map(h => (
                  <th key={h} style={TH}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {displayRows.map(r => (
                  <Row
                    key={r.transactionId}
                    row={r}
                    view={view}
                    onOpen={() => openDetail(r)}
                    onApprove={() => runApproveRefund(r)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Side drawer */}
      {selected && (
        <DetailDrawer
          txn={selected}
          onClose={closeDetail}
          onAction={onAction}
          onSync={syncWithRazorpay}
          actionBusy={actionBusy}
          syncing={syncing}
        />
      )}
    </div>
  );
}

// ???????????????????????????????????????????????????????????????????????????
// Sub-tabs (All | Payments | Refunds)
// ???????????????????????????????????????????????????????????????????????????
function SubTabs({ view, setView, totals, allCount }) {
  const tabs = [
    { id:'all',      label:'All',      icon:<MdSwapHoriz/>, count: (allCount != null ? allCount : (totals?.total || 0)) },
    { id:'razorpay', label:'Razorpay', icon:<MdPayments/>,  count: (totals?.razorpayLive || 0) },
    { id:'payments', label:'Payments', icon:<MdPayments/>,  count: (totals?.byType?.payment || 0) },
    { id:'refunds',  label:'Refunds',  icon:<MdAssignmentReturn/>, count: (totals?.byType?.refund || 0) },
  ];
  return (
    <div style={{
      marginTop:14, display:'flex', gap:8, flexWrap:'wrap',
      background:CARD, border:`1px solid ${BORDER}`, borderRadius:12, padding:8,
    }}>
      {tabs.map(t => {
        const active = view === t.id;
        return (
          <button key={t.id} onClick={()=>setView(t.id)} style={{
            flex:'1 1 160px',
            padding:'10px 14px', borderRadius:8, border:'none', cursor:'pointer',
            background: active
              ? (t.id === 'refunds' ? '#0ea5e922' : (t.id === 'payments' || t.id === 'razorpay') ? '#60a5fa22' : `${ACCENT}22`)
              : 'transparent',
            color: active
              ? (t.id === 'refunds' ? '#0ea5e9' : (t.id === 'payments' || t.id === 'razorpay') ? '#60a5fa' : ACCENT)
              : TEXT,
            fontWeight: active ? 800 : 600, fontFamily:'inherit', fontSize:13,
            display:'flex', alignItems:'center', justifyContent:'center', gap:8,
            transition:'background 0.15s',
          }}>
            {t.icon}
            <span>{t.label}</span>
            <span style={{
              background: active ? 'rgba(0,0,0,0.25)' : BORDER,
              padding:'1px 8px', borderRadius:999, fontSize:11, fontWeight:800,
            }}>{t.count}</span>
          </button>
        );
      })}
    </div>
  );
}

// ???????????????????????????????????????????????????????????????????????????
// Stat cards (context-aware)
// ???????????????????????????????????????????????????????????????????????????
function StatGrid({ totals, view, rowCount = 0 }) {
  const stats = useMemo(() => {
    const t = totals || {};
    if (view === 'razorpay') {
      return [
        { label:'Razorpay-linked rows',    val: t.razorpayLive || 0,                    color:BLUE,   icon:<MdPayments/> },
        { label:'Captured (paid)',         val: t.paymentsSuccess || 0,                   color:GREEN,  icon:<MdCheckCircle/> },
        { label:'Razorpay refunds',        val: t.refundsCredited || 0,                   color:GREEN,  icon:<MdAssignmentReturn/> },
        { label:'Historical (no gateway)', val: t.refundsHistorical || 0,               color:MUTED,  icon:<MdReceiptLong/> },
        { label:'Pending at gateway',      val: t.refundsPending || 0,                    color:ACCENT, icon:<MdPending/> },
        { label:'Net revenue',             val: fmtRS(t.netRevenue || 0),                 color:PURPLE, icon:<MdAccountBalanceWallet/> },
      ];
    }
    if (view === 'refunds') {
      return [
        { label:'Total refunds',           val: t.byType?.refund || 0,                     color:BLUE,   icon:<MdAssignmentReturn/> },
        { label:'₹ Razorpay credited',     val: fmtRS(t.refundsCreditedAmount || 0),      color:GREEN,  icon:<MdAccountBalanceWallet/> },
        { label:'Historical reconciled',   val: t.refundsHistorical || 0,                 color:MUTED,  icon:<MdReceiptLong/> },
        { label:'Pending action',          val: t.refundsPending || 0,                    color:ACCENT, icon:<MdPending/> },
        { label:'Gateway credited',        val: t.refundsCredited || 0,                   color:GREEN,  icon:<MdCheckCircle/> },
        { label:'Failed',                  val: t.refundsFailed || 0,                     color:RED,    icon:<MdError/> },
      ];
    }
    if (view === 'payments') {
      return [
        { label:'Bookings paid',           val: t.paymentsSuccess || 0,                   color:GREEN,  icon:<MdCheckCircle/> },
        { label:'Revenue (success)',       val: fmtRS(t.amountTotal || 0),                color:GREEN,  icon:<MdPayments/> },
        { label:'Failed payments',         val: t.paymentsFailed || 0,                    color:RED,    icon:<MdError/> },
        { label:'Pending',                 val: (t.byStatus?.pending || 0),               color:ACCENT, icon:<MdPending/> },
        { label:'Net revenue',             val: fmtRS(t.netRevenue || 0),                 color:PURPLE, icon:<MdAccountBalanceWallet/> },
        { label:'Total payments',          val: t.byType?.payment || 0,                   color:TEXT,   icon:<MdReceiptLong/> },
      ];
    }
    // ALL view (default)
    return [
      { label:'Bookings paid',           val: t.paymentsSuccess || 0,                   color:GREEN,  icon:<MdCheckCircle/> },
      { label:'Revenue (success)',       val: fmtRS(t.amountTotal || 0),                color:GREEN,  icon:<MdPayments/> },
      { label:'Refunds credited',        val: fmtRS(t.refundsCreditedAmount || 0),      color:BLUE,   icon:<MdAssignmentReturn/> },
      { label:'Net revenue',             val: fmtRS(t.netRevenue || 0),                 color:PURPLE, icon:<MdAccountBalanceWallet/> },
      { label:'Pending refunds',         val: t.refundsPending || 0,                    color:ACCENT, icon:<MdPending/> },
      { label:'Total records',           val: (t.total != null ? t.total : rowCount),     color:'#f8fafc', icon:<MdAccessTime/> },
    ];
  }, [totals, view, rowCount]);

  return (
    <div style={{display:'grid', gap:12, gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))'}}>
      {stats.map((s, i) => (
        <div key={i} style={{
          background:CARD, border:`1px solid ${BORDER}`, borderRadius:12,
          padding:14, display:'flex', alignItems:'center', gap:12,
        }}>
          <div style={{
            width:38, height:38, borderRadius:10,
            background:`${s.color}22`, color:s.color,
            display:'grid', placeItems:'center', fontSize:20,
          }}>{s.icon}</div>
          <div>
            <div style={{color:MUTED, fontSize:11, letterSpacing:0.4, textTransform:'uppercase', fontWeight:800}}>{s.label}</div>
            <div style={{color:s.color, fontSize:22, fontWeight:900, marginTop:3, lineHeight:1.1}}>{s.val}</div>
            {s.sub && <div style={{color:MUTED, fontSize:11, marginTop:2, fontWeight:700}}>{s.sub}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// modeOfRefund — infer which refund mode (Auto / Semi-Auto / Manual) processed
// a refund row. The platform setting (refundMode) isn't currently stamped on
// the row, so we derive it from observable signals:
//
//   • status === 'requested'                  → "Manual" (sitting in queue;
//                                                no automation acted on it)
//   • cancelledBy === 'admin'                 → "Manual" (admin override path)
//   • cancelledBy === 'hospital' & credited   → "Auto"   (only Auto credits
//                                                hospital cancels instantly)
//   • cancelledBy === 'hospital' & requested  → "Semi-Auto" or "Manual"
//   • cancelledBy === 'patient'  & credited   → "Auto" or "Semi-Auto"
//                                                (both auto-credit patients)
//   • cancelledBy === 'patient'  & requested  → "Manual"
//
// This is best-effort — if the backend later adds a `refundMode` field on
// the transaction row, this falls through to that value first.
// ─────────────────────────────────────────────────────────────────────────────
function modeOfRefund(r) {
  if (r?.refundMode) return String(r.refundMode).toLowerCase();
  const by  = String(r?.cancelledBy || '').toLowerCase();
  const st  = String(r?.status      || '').toLowerCase();
  const credited = ['credited', 'processed', 'initiated'].includes(st);

  if (by === 'admin') return 'manual';
  if (st === 'requested') {
    // Hospital-cancel + Requested means semi-auto held it for admin approval.
    return by === 'hospital' ? 'semi-auto' : 'manual';
  }
  if (credited) return 'auto';
  return 'manual';
}
const MODE_LABEL = { 'auto':'Auto', 'semi-auto':'Semi-Auto', 'manual':'Manual' };
const MODE_COLOR = { 'auto':GREEN, 'semi-auto':ACCENT, 'manual':BLUE };

// Who raised the refund — colour-coded so admins can see at a glance whether
// the customer asked for it or the hospital cancelled the booking.
const RAISED_BY_COLOR = { patient: BLUE, hospital: RED, admin: PURPLE };
const RAISED_BY_LABEL = { patient: 'Patient', hospital: 'Hospital', admin: 'Admin' };


// ???????????????????????????????????????????????????????????????????????????
// Table headers per sub-tab
// ???????????????????????????????????????????????????????????????????????????
function headersFor(view) {
  if (view === 'refunds') {
    // Refund-specific columns: who raised it, which mode handled it, and an
    // inline Approve action so admins don't have to open the drawer to act.
    return ['Created','Raised By','Mode','User','Hospital','Amount','%','Status','Refund Ref','Action'];
  }
  if (view === 'payments') {
    // Razorpay-parity columns — Payment ID, Bank RRN, Customer (with method),
    // Created, Amount, Status, Details. Matches dashboard.razorpay.com.
    return ['Payment ID','Bank RRN','Customer detail','Created on','Amount','Status','—'];
  }
  return ['Created','Type','User','Hospital','Amount','Status','Reference','—'];
}

// ???????????????????????????????????????????????????????????????????????????
// Table row (different layout per sub-tab)
// ???????????????????????????????????????????????????????????????????????????
function Row({ row, view, onOpen, onApprove }) {
  const dispStatus = displayTxnStatus(row);
  const color = STATUS_COLOR[dispStatus] || STATUS_COLOR[row.status] || SUBTLE;
  const hover = (e, on) => {
    e.currentTarget.style.background = on ? 'rgba(148,163,184,0.06)' : 'transparent';
  };
  const stop = (e) => e.stopPropagation();

  if (view === 'refunds') {
    const ref = row.gatewayRefundId || row.gatewayPaymentId || row.transactionId;
    const raisedBy = String(row.cancelledBy || '').toLowerCase();
    const raisedByColor = RAISED_BY_COLOR[raisedBy] || SUBTLE;
    const raisedByLabel = RAISED_BY_LABEL[raisedBy] || (row.cancelledBy || '—');
    const mode = modeOfRefund(row);
    const modeColor = MODE_COLOR[mode] || SUBTLE;
    const canApprove = row.status === 'requested';
    const synthetic = isSyntheticBackfill(row);
    return (
      <tr onClick={onOpen} style={ROW}
          onMouseEnter={e=>hover(e,true)} onMouseLeave={e=>hover(e,false)}>
        <td style={{...TD, color:SUBTLE, fontWeight:700}}>{fmtRowWhen(row)}</td>

        {/* Who raised the refund — Patient / Hospital / Admin */}
        <td style={TD}>
          <span style={pill(raisedByColor)} title={POLICY_LABEL[row.refundPolicy] || ''}>
            {raisedByLabel}
          </span>
        </td>

        {/* Refund mode (Auto / Semi-Auto / Manual) inferred from the row */}
        <td style={TD}>
          <span style={pill(modeColor)}>{MODE_LABEL[mode] || mode}</span>
        </td>

        <td style={TD}>
          <div style={{fontWeight:800, color:TEXT}}>{row.userName || '—'}</div>
          <div style={{color:SUBTLE, fontSize:12, fontWeight:700}}>{row.userPhone || ''}</div>
        </td>
        <td style={{...TD, maxWidth:160}}>
          <div style={{...ELLIPSIS, fontWeight:800, color:TEXT}}>{row.hospitalName || row.hospitalId || '—'}</div>
        </td>
        <td style={{...TD, fontWeight:900, color:GREEN, fontSize:14}}>+{fmtRS(row.amount)}</td>
        <td style={TD}>{row.refundPercent ? `${row.refundPercent}%` : '—'}</td>
        <td style={TD}>
          <span style={pill(color)}>{STATUS_LABEL[dispStatus] || dispStatus}</span>
          {isSyntheticBackfill(row) && (
            <div style={{ fontSize:10, color:MUTED, marginTop:3 }}>No Razorpay API</div>
          )}
        </td>
        <td style={{...TD, fontFamily:'monospace', fontSize:12.5, fontWeight:800}}>
          <CopyInline value={ref} label={short(ref, 14)}/>
        </td>

        {/* Inline Approve — only for status='requested' rows. Otherwise just
            opens the drawer. Stops row-click bubbling so the confirm dialog
            doesn't double-fire with the drawer. */}
        <td style={TD} onClick={stop}>
          {canApprove ? (
            <button onClick={onApprove} style={{
              padding:'4px 10px', borderRadius:6,
              border:`1px solid ${synthetic ? ACCENT : GREEN}`,
              background: synthetic ? `${ACCENT}22` : `${GREEN}22`,
              color: synthetic ? ACCENT : GREEN,
              fontWeight:800, fontSize:11, fontFamily:'inherit', cursor:'pointer',
              display:'inline-flex', alignItems:'center', gap:4,
            }}><MdCheck size={13}/> {synthetic ? 'Manual' : 'Approve'}</button>
          ) : (
            <span onClick={onOpen} style={{color:ACCENT, fontSize:13, fontWeight:900, cursor:'pointer'}}>Details →</span>
          )}
        </td>
      </tr>
    );
  }

  if (view === 'payments') {
    // Razorpay-parity layout: Payment ID first, Bank RRN, Customer detail
    // (phone + payment-method subtext like "UPI" / "Credit Card"), then
    // Created / Amount / Status / Details. Mirrors what dashboard.razorpay.com
    // shows so admins switching between the two tools don't get lost.
    const payId  = row.gatewayPaymentId || row.transactionId;
    const method = paymentMethodLabel(row);
    return (
      <tr onClick={onOpen} style={ROW}
          onMouseEnter={e=>hover(e,true)} onMouseLeave={e=>hover(e,false)}>
        <td style={{...TD, fontFamily:'monospace', fontSize:12.5, fontWeight:800}}>
          <CopyInline value={payId} label={short(payId, 18)}/>
        </td>
        <td style={{...TD, fontFamily:'monospace', fontSize:12.5, color:SUBTLE, fontWeight:800}}>
          {row.bankRrn || '—'}
          {method && <div style={{color:MUTED, fontSize:11, marginTop:2, fontFamily:'inherit', fontWeight:700}}>{method}</div>}
        </td>
        <td style={TD}>
          <div style={{fontWeight:900, color:TEXT}}>{row.userPhone || row.gatewayContact || '—'}</div>
          <div style={{color:SUBTLE, fontSize:12, fontWeight:700}}>{row.userName || 'NA'}</div>
        </td>
        <td style={{...TD, color:SUBTLE, fontWeight:700}}>{fmtRowWhen(row)}</td>
        <td style={{...TD, fontWeight:900, color:TEXT, fontSize:14}}>{fmtRS(row.amount)}</td>
        <td style={TD}>
          <span style={pill(color)}>{STATUS_LABEL[dispStatus] || dispStatus}</span>
          {row.razorpayStatus && (
            <div style={{ fontSize:10, color:MUTED, marginTop:3 }}>Razorpay: {row.razorpayStatus}</div>
          )}
        </td>
        <td style={TD}><span style={{color:BLUE, fontSize:13, fontWeight:900}}>Details ›</span></td>
      </tr>
    );
  }

  // ─── ALL view (Razorpay/Cashfree-style: payment + refund = ONE ledger row)
  // Merge logic in displayRows attaches __refundLinked to a payment when a
  // refund exists for that booking, and flips status to refunded /
  // partially_refunded. We render those as a single PAYMENT row whose status
  // pill reflects the refund state, plus a "−₹X refunded" subline under the
  // amount — exactly how Razorpay and Cashfree present it.
  const isRefund   = row.type === 'refund';
  const isRefunded = !isRefund && ['refunded', 'partially_refunded'].includes(String(row.status || '').toLowerCase());
  const refundedAmt = Number(row.__refundCredited || 0);
  const typePillBg  = isRefund                ? '#0ea5e922'
                    : isRefunded              ? `${BLUE}22`
                    :                            '#60a5fa22';
  const typePillFg  = isRefund                ? '#0ea5e9'
                    : isRefunded              ? BLUE
                    :                            '#60a5fa';
  const typeLabel   = isRefund                ? 'REFUND'
                    : isRefunded              ? 'REFUNDED'
                    :                            'PAYMENT';
  const ref = isRefund
    ? (row.gatewayRefundId || row.gatewayPaymentId || row.transactionId)
    : (row.gatewayPaymentId || row.gatewayOrderId  || row.transactionId);
  return (
    <tr onClick={onOpen} style={ROW}
        onMouseEnter={e=>hover(e,true)} onMouseLeave={e=>hover(e,false)}>
      <td style={{...TD, color:SUBTLE, fontWeight:700}}>{fmtRowWhen(row)}</td>
      <td style={TD}>
        <span style={{
          padding:'2px 8px', borderRadius:999, fontSize:10.5, fontWeight:800,
          background: typePillBg, color: typePillFg,
        }}>{typeLabel}</span>
        {row.__refundPending && (
          <div style={{marginTop:3, fontSize:9.5, color:ACCENT, fontWeight:800, letterSpacing:0.4}}>
            REFUND PENDING
          </div>
        )}
      </td>
      <td style={TD}>
        <div style={{fontWeight:900, color:TEXT}}>{row.userName || '—'}</div>
        <div style={{color:SUBTLE, fontSize:12, fontWeight:700}}>{row.userPhone || ''}</div>
      </td>
      <td style={{...TD, maxWidth:180}}>
        <div style={{...ELLIPSIS, fontWeight:800, color:TEXT}}>{row.hospitalName || row.hospitalId || '—'}</div>
      </td>
      <td style={{...TD, fontWeight:900, color: isRefund ? GREEN : TEXT, fontSize:14}}>
        {isRefund ? '+' : ''}{fmtRS(row.amount || 0)}
        {isRefunded && refundedAmt > 0 && (
          <div style={{color:BLUE, fontSize:11.5, fontWeight:900, marginTop:2}}>
            −{fmtRS(refundedAmt)} refunded
          </div>
        )}
      </td>
      <td style={TD}>
        <span style={pill(color)}>{STATUS_LABEL[dispStatus] || dispStatus}</span>
        {row.razorpayStatus && !isSyntheticBackfill(row) && (
          <div style={{ fontSize:10, color:MUTED, marginTop:3 }}>Razorpay: {row.razorpayStatus}</div>
        )}
      </td>
      <td style={{...TD, fontFamily:'monospace', fontSize:12.5, fontWeight:800}}>
        <CopyInline value={ref} label={short(ref, 16)}/>
      </td>
      <td style={TD}><span style={{color:ACCENT, fontSize:13, fontWeight:900}}>Details →</span></td>
    </tr>
  );
}

const ROW = { cursor:'pointer', borderTop:`1px solid ${BORDER}`, transition:'background 0.12s', color:TEXT };
const ELLIPSIS = { overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' };
const pill = (color) => ({
  padding:'4px 10px', borderRadius:999, fontSize:11, fontWeight:900,
  background:`${color}28`, color,
});
const short = (s, n) => {
  if (!s) return '—';
  const str = String(s);
  if (str.length <= n) return str;
  return str.slice(0, n - 1) + '…';
};

function CopyInline({ value, label }) {
  // Hooks must run on every render — keep the state above the early return.
  const [copied, setCopied] = useState(false);
  if (!value) return <span style={{color:SUBTLE}}>—</span>;
  return (
    <span
      onClick={(e)=>{
        e.stopPropagation();
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(()=>setCopied(false), 900);
      }}
      title={value}
      style={{
        display:'inline-flex', alignItems:'center', gap:4,
        color:TEXT, cursor:'pointer', fontWeight:800,
      }}>
      <span>{label}</span>
      {copied
        ? <MdCheck size={12} color={GREEN}/>
        : <MdContentCopy size={11} color={SUBTLE}/>}
    </span>
  );
}

// ???????????????????????????????????????????????????????????????????????????
// Side drawer — Razorpay-style full transaction detail.
// Two-column layout (Details on the left, Timeline on the right) modelled
// directly on dashboard.razorpay.com/app/payments/<id>. On narrow viewports
// the timeline collapses below the details so nothing is hidden.
// ???????????????????????????????????????????????????????????????????????????
function DetailDrawer({ txn, onClose, onAction, onSync, actionBusy, syncing }) {
  const dispStatus = displayTxnStatus(txn);
  const color = STATUS_COLOR[dispStatus] || STATUS_COLOR[txn.status] || SUBTLE;
  const canSyncRazorpay = hasRazorpayGatewayId(txn) && !isSyntheticBackfill(txn);
  const isRefund = txn.type === 'refund';
  const pendingRef = pendingLinkedRefund(txn);
  const creditedAmt = Number(txn.__refundCredited ?? txn.refundedAmount ?? 0);
  const paidAmount = Number(txn.amount || 0);
  const synthetic = isSyntheticBackfill(txn);
  const canInitiate = isRefund && txn.status === 'requested' && !synthetic;
  const canMarkRefundManual = isRefund && txn.status === 'requested' && synthetic;
  const canIssueGatewayRefund = !isRefund
    && ['success', 'partially_refunded'].includes(txn.status)
    && creditedAmt < paidAmount
    && !txn.__refundPending
    && !synthetic;
  const canMarkPaymentManual = !isRefund && synthetic && (pendingRef || txn.__refundPending);

  const gw       = (txn.gatewayProvider || 'razorpay').toLowerCase();
  const gwLabel  = gw === 'backfill' ? 'Historical (reconciled)' : (gw === 'cashfree' ? 'Cashfree (legacy)' : 'Razorpay');

  // Pretty-print payment method (UPI · vpa, Card · network/last4, etc.).
  const methodLine = (() => {
    const m = (txn.paymentMethod || '').toLowerCase();
    if (!m) return null;
    if (m === 'upi' && txn.upiVpa)   return `UPI · ${txn.upiVpa}`;
    if (m === 'upi')                  return 'UPI';
    if (m === 'card') {
      const bits = [];
      if (txn.cardNetwork) bits.push(txn.cardNetwork);
      if (txn.cardType)    bits.push(txn.cardType);
      const head = bits.length ? bits.join(' ') : 'Card';
      return txn.cardLast4 ? `${head} · ••${txn.cardLast4}` : head;
    }
    if (m === 'netbanking') return (txn.bank || txn.bankName) ? `Netbanking · ${txn.bank || txn.bankName}` : 'Netbanking';
    if (m === 'wallet')      return (txn.wallet || txn.walletName) ? `Wallet · ${txn.wallet || txn.walletName}` : 'Wallet';
    return txn.methodDetail || (m.charAt(0).toUpperCase() + m.slice(1));
  })();

  return (
    <>
      <div onClick={onClose} style={{
        position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:40,
      }}/>
      <div style={{
        position:'fixed', top:0, right:0, bottom:0, width:'min(960px, 100vw)',
        background:BG, borderLeft:`1px solid ${BORDER}`, zIndex:41,
        overflowY:'auto', fontFamily:'inherit', color:TEXT,
      }}>
        {/* Header — sticky title bar with status pill + close */}
        <div style={{
          padding:16, borderBottom:`1px solid ${BORDER}`,
          display:'flex', alignItems:'center', gap:12, position:'sticky', top:0,
          background:BG, zIndex:2,
        }}>
          <button onClick={onClose} style={{
            background:'transparent', border:`1px solid ${BORDER}`, color:SUBTLE,
            cursor:'pointer', fontSize:13, padding:'5px 10px', borderRadius:8,
            display:'inline-flex', alignItems:'center', gap:4, fontFamily:'inherit',
          }}>‹ Go Back</button>
          <div style={{flex:1}}/>
          <button onClick={onClose} style={{
            background:'transparent', border:'none', color:TEXT, cursor:'pointer', fontSize:22,
          }}><MdClose/></button>
        </div>

        {/* Razorpay-style hero — big icon + amount + status badge + (if refunded) sub-line */}
        <div style={{padding:'20px 24px 16px', borderBottom:`1px solid ${BORDER}`}}>
          <div style={{display:'flex', alignItems:'center', gap:14}}>
            <div style={{
              width:56, height:56, borderRadius:12, display:'grid', placeItems:'center',
              background: isRefund ? `${BLUE}22` : `${GREEN}22`,
              color:      isRefund ? BLUE : GREEN, fontSize:30,
            }}>
              {isRefund ? <MdAssignmentReturn/> : (txn.status === 'success' ? <MdCheckCircle/> : <MdPayments/>)}
            </div>
            <div style={{flex:1, minWidth:0}}>
              <div style={{display:'flex', alignItems:'center', gap:10, flexWrap:'wrap'}}>
                <span style={{fontSize:30, fontWeight:900, color:TEXT}}>
                  {isRefund ? '+' : ''}{fmtRS(txn.amount)}
                </span>
                <span style={pill(color)}>{STATUS_LABEL[dispStatus] || dispStatus}</span>
                {txn.razorpayStatus && (
                  <span style={{...pill(BLUE), fontSize:10}}>Razorpay: {txn.razorpayStatus}</span>
                )}
              </div>
              <div style={{color:SUBTLE, fontSize:12.5, marginTop:4}}>
                Created on: {fmtDT(txn.createdAt)}
                {txn.razorpaySyncedAt && <> · Synced {fmtDT(txn.razorpaySyncedAt)}</>}
              </div>
              {/* Refund summary — shown on payments that were refunded */}
              {!isRefund && txn.status === 'refunded' && txn.refundedAmount && (
                <div style={{color:SUBTLE, fontSize:12.5, marginTop:2}}>
                  Refund of <strong style={{color:TEXT}}>{fmtRS(txn.refundedAmount)}</strong>
                  {txn.refundedAt && <> issued on <strong style={{color:TEXT}}>{fmtDT(txn.refundedAt)}</strong></>}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Two-column body: DETAILS (left) + TIMELINE (right rail) */}
        <div style={{
          display:'grid', gridTemplateColumns:'minmax(0, 1fr) 280px',
          gap:0, alignItems:'flex-start',
        }}>
          {/* LEFT — Details, Customer, Refund breakdown, Reference IDs, Notes */}
          <div style={{borderRight:`1px solid ${BORDER}`}}>
            {/* Details (Razorpay parity: Payment ID, Bank RRN, Order ID, Method, etc.) */}
            <Section title='DETAILS'>
              <CopyRow label={isRefund ? 'Refund ID' : 'Payment ID'}
                value={isRefund ? (txn.gatewayRefundId || txn.transactionId) : (txn.gatewayPaymentId || txn.transactionId)}
                highlight/>
              {txn.bankRrn         && <CopyRow label='Bank RRN'   value={txn.bankRrn}/>}
              {txn.authCode        && <Kv     label='Auth Code'   value={txn.authCode}/>}
              {txn.gatewayOrderId  && <CopyRow label='Order ID'   value={txn.gatewayOrderId}/>}
              {txn.bookingId       && <CopyRow label='Booking ID' value={txn.bookingId}/>}
              {methodLine          && <Kv     label='Payment method' value={methodLine}/>}
            </Section>

            {/* Amount breakdown — base + platform fee + GST = total paid.
                THE answer to "they paid 200 + fee + GST but it only showed 200".
                Reads the snapshot the backend stores on the row
                (breakdownOpFee / breakdownPatientPlatformFee / breakdownPatientGst);
                for older rows that lack the snapshot we derive it from the gross
                using the platform fee rule so the section ALWAYS shows. */}
            {!isRefund && (() => {
              const bd = amountBreakdown(txn);
              if (!bd) return null;
              return (
                <Section title='AMOUNT BREAKDOWN'>
                  <Kv label='Consultation (base)' value={fmtMoney(bd.base)}/>
                  <Kv label="Platform fee" value={fmtMoney(bd.fee)}/>
                  <Kv label={`GST${bd.gstPct ? ` (${bd.gstPct}% on fee)` : ' on platform fee'}`} value={fmtMoney(bd.gst)}/>
                  {bd.discount > 0 && <Kv label='Discount' value={`-${fmtMoney(bd.discount)}`} valueColor={BLUE}/>}
                  <div style={{borderTop:`1px solid ${BORDER}`, marginTop:6, paddingTop:6}}>
                    <Kv label='Total paid by patient' value={fmtMoney(bd.total)} valueColor={GREEN} highlight/>
                  </div>
                  <div style={{marginTop:8, fontSize:11, color:SUBTLE, lineHeight:1.5}}>
                    Hospital receives the <strong style={{color:TEXT}}>base</strong> amount; HCEN keeps the
                    <strong style={{color:TEXT}}> platform fee</strong>; the
                    <strong style={{color:TEXT}}> GST</strong> is remitted to the government.
                    {bd.derived && ' (Reconstructed from the total for a historical row.)'}
                  </div>
                </Section>
              );
            })()}

            {/* Customer */}
            <Section title='CUSTOMER DETAILS'>
              <Kv label='Name'  value={txn.userName  || '—'}/>
              <Kv label='Phone' value={txn.userPhone || txn.gatewayContact || '—'}/>
              {(txn.userEmail || txn.gatewayEmail) && <Kv label='Email' value={txn.userEmail || txn.gatewayEmail}/>}
            </Section>

            {/* Hospital + booking context — what makes our admin BETTER than Razorpay's */}
            {(txn.hospitalName || txn.hospitalId || txn.doctorName) && (
              <Section title='BOOKING CONTEXT'>
                {txn.hospitalName && <Kv label='Hospital' value={txn.hospitalName}/>}
                {txn.hospitalId   && <CopyRow label='Hospital ID' value={txn.hospitalId}/>}
                {txn.doctorName   && <Kv label='Doctor' value={txn.doctorName}/>}
                {txn.appointmentDate && <Kv label='Appointment' value={txn.appointmentDate}/>}
              </Section>
            )}

            {/* Razorpay's OWN gateway fee (distinct from the HCEN platform fee
                above) — what Razorpay charges HCEN to process the payment.
                Stored in PAISE by the backend (gatewayFeePaise / gatewayTaxPaise). */}
            {(txn.gatewayFeePaise != null || txn.gatewayTaxPaise != null) && (
              <Section title='RAZORPAY GATEWAY FEE'>
                <Kv label='Gateway fee' value={fmtMoney((Number(txn.gatewayFeePaise || 0)) / 100)}/>
                <Kv label='GST on gateway fee' value={fmtMoney((Number(txn.gatewayTaxPaise || 0)) / 100)}/>
                <Kv label='Bearer' value='HCEN (deducted from settlement by Razorpay)'/>
              </Section>
            )}

            {/* Refund-specific breakdown */}
            {isRefund && (
              <Section title='REFUND BREAKDOWN'>
                <Kv
                  label='Raised by'
                  value={(RAISED_BY_LABEL[String(txn.cancelledBy || '').toLowerCase()] || (txn.cancelledBy || '—')).toString().toUpperCase()}
                  valueColor={RAISED_BY_COLOR[String(txn.cancelledBy || '').toLowerCase()] || TEXT}
                  highlight
                />
                <Kv
                  label='Mode'
                  value={MODE_LABEL[modeOfRefund(txn)] || modeOfRefund(txn)}
                  valueColor={MODE_COLOR[modeOfRefund(txn)] || TEXT}
                  highlight
                />
                <Kv label='Policy applied' value={POLICY_LABEL[txn.refundPolicy] || (txn.refundPolicy || '—').toUpperCase()} highlight/>
                <Kv label='Amount'         value={fmtRS(txn.amount)} valueColor={GREEN}/>
                {txn.refundPercent > 0 && <Kv label='Percent' value={`${txn.refundPercent}%`}/>}
                {txn.refundSpeed   && <Kv label='Refund speed' value={String(txn.refundSpeed).toUpperCase()}/>}
                {txn.refundReason  && <Kv label='Reason' value={txn.refundReason}/>}
                {txn.creditedAt    && <Kv label='Credited on' value={fmtDT(txn.creditedAt)}/>}
                {txn.parentTransactionId && <CopyRow label='Original payment' value={txn.parentTransactionId}/>}
              </Section>
            )}

            {/* Description + Notes — last because they're free-form/optional */}
            <Section title='DESCRIPTION'>
              <Kv label='Description' value={txn.description || 'Doctor Consultation'}/>
              <div style={{marginTop:10, padding:'10px 12px', background:'var(--surface-subtle)', border:`1px solid ${BORDER}`, borderRadius:8, fontSize:12, fontFamily:'monospace', color:SUBTLE, lineHeight:1.6}}>
                <strong style={{color:TEXT}}>booking_id:</strong> {txn.bookingId || '—'}<br/>
                <strong style={{color:TEXT}}>customer_phone:</strong> {txn.userPhone || '—'}<br/>
                <strong style={{color:TEXT}}>customer_name:</strong> {txn.userName || '—'}<br/>
                {txn.userEmail && <><strong style={{color:TEXT}}>customer_email:</strong> {txn.userEmail}<br/></>}
                <strong style={{color:TEXT}}>hospital_id:</strong> {txn.hospitalId || '—'}<br/>
                <strong style={{color:TEXT}}>hospital_name:</strong> {txn.hospitalName || '—'}
              </div>
            </Section>

            {/* Gateway reference IDs (collapsed under one section) */}
            <Section title='REFERENCE IDS'>
              <CopyRow label='Transaction ID' value={txn.transactionId}/>
              {txn.gatewayPaymentId    && <CopyRow label={`${gwLabel} Payment ID`} value={txn.gatewayPaymentId}/>}
              {txn.gatewayRefundId     && <CopyRow label={`${gwLabel} Refund ID (ARN)`} value={txn.gatewayRefundId}/>}
              {txn.bankTxnId           && <CopyRow label='Bank Txn ID' value={txn.bankTxnId}/>}
              <Kv label='Gateway' value={gwLabel}/>
            </Section>
          </div>

          {/* RIGHT — Timeline rail (sticky on tall content) */}
          <div style={{padding:'18px 18px 18px', background:'var(--surface-subtle)'}}>
            <div style={{color:SUBTLE, fontSize:10.5, fontWeight:800, letterSpacing:1.5, marginBottom:14}}>
              TIMELINE
            </div>
            <Timeline txn={txn}/>

            {synthetic && (
              <div style={{
                marginTop:14, padding:'10px 12px', borderRadius:8, fontSize:11.5, lineHeight:1.5,
                background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.35)', color:ACCENT,
              }}>
                Reconciled from Bookings — no live Razorpay charge. Use &quot;Mark as manually credited&quot; if you paid the customer offline.
              </div>
            )}

            {canSyncRazorpay && (
              <button disabled={syncing || actionBusy} onClick={() => onSync?.(txn)} style={{
                marginTop:14, width:'100%', padding:'10px 12px', borderRadius:8,
                background:`${BLUE}22`, border:`1px solid ${BLUE}`, color:BLUE,
                fontWeight:800, fontSize:13, fontFamily:'inherit',
                cursor: (syncing || actionBusy) ? 'wait' : 'pointer',
                opacity: (syncing || actionBusy) ? 0.65 : 1,
                display:'inline-flex', justifyContent:'center', alignItems:'center', gap:6,
              }}>
                <MdRefresh size={16}/> {syncing ? 'Syncing…' : 'Sync status from Razorpay'}
              </button>
            )}

            {canIssueGatewayRefund && (
              <button disabled={actionBusy} onClick={()=>onAction('issue-refund')} style={{
                marginTop:16, width:'100%', padding:'10px 12px', borderRadius:8,
                background:BLUE, border:`1px solid ${BLUE}`, color:'#fff',
                fontWeight:800, fontSize:13, fontFamily:'inherit',
                cursor: actionBusy ? 'wait' : 'pointer', opacity: actionBusy ? 0.65 : 1,
                display:'inline-flex', justifyContent:'center', alignItems:'center', gap:6,
              }}>
                <MdAssignmentReturn size={16}/> Issue refund via Razorpay
              </button>
            )}
            {canMarkPaymentManual && (
              <button disabled={actionBusy} onClick={()=>onAction('mark-manual', { refundId: pendingRef?.transactionId })} style={{
                marginTop:16, width:'100%', padding:'10px 12px', borderRadius:8,
                background:`${ACCENT}22`, border:`1px solid ${ACCENT}`, color:ACCENT,
                fontWeight:800, fontSize:13, fontFamily:'inherit',
                cursor: actionBusy ? 'wait' : 'pointer', opacity: actionBusy ? 0.65 : 1,
                display:'inline-flex', justifyContent:'center', alignItems:'center', gap:6,
              }}>
                <MdCheckCircle size={16}/> Mark refund settled (manual)
              </button>
            )}
          </div>
        </div>

        {/* Sticky action bar at bottom — Approve (refund) + Add note */}
        <div style={{padding:16, borderTop:`1px solid ${BORDER}`, display:'flex', gap:10, flexWrap:'wrap', position:'sticky', bottom:0, background:BG}}>
          {canInitiate && (
            <button disabled={actionBusy} onClick={()=>onAction('initiate')} style={{...BTN, background:GREEN, borderColor:GREEN, color:'#05140c', opacity: actionBusy ? 0.65 : 1, cursor: actionBusy ? 'wait' : 'pointer'}}>
              <MdCheckCircle/> Approve & credit at Razorpay
            </button>
          )}
          {canMarkRefundManual && (
            <button disabled={actionBusy} onClick={()=>onAction('mark-manual')} style={{...BTN, background:`${ACCENT}22`, borderColor:ACCENT, color:ACCENT, opacity: actionBusy ? 0.65 : 1, cursor: actionBusy ? 'wait' : 'pointer'}}>
              <MdCheckCircle/> Mark as manually credited
            </button>
          )}
          <button disabled={actionBusy} onClick={()=>onAction('note')} style={BTN_GHOST}>
            <MdStickyNote2/> Add note
          </button>
        </div>
      </div>
    </>
  );
}

function Section({ title, children }) {
  return (
    <div style={{padding:'14px 20px', borderTop:`1px solid ${BORDER}`}}>
      <div style={{color:SUBTLE, fontSize:10.5, fontWeight:800, letterSpacing:1.5, marginBottom:10}}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Kv({ label, value, valueColor, highlight }) {
  return (
    <div style={{display:'flex', justifyContent:'space-between', gap:10, padding:'6px 0'}}>
      <div style={{color:SUBTLE, fontSize:12.5}}>{label}</div>
      <div style={{
        color: valueColor || TEXT,
        fontSize:13, fontWeight:700,
        letterSpacing: highlight ? 0.5 : 0,
      }}>{value}</div>
    </div>
  );
}

function CopyRow({ label, value, highlight }) {
  const [copied, setCopied] = useState(false);
  return (
    <div onClick={()=>{
      navigator.clipboard.writeText(value);
      setCopied(true); setTimeout(()=>setCopied(false), 900);
    }}
      style={{display:'flex', justifyContent:'space-between', gap:10, padding:'7px 0', cursor:'pointer'}}
      title='Click to copy'>
      <div style={{color:SUBTLE, fontSize:12.5}}>{label}</div>
      <div style={{display:'flex', alignItems:'center', gap:6, overflow:'hidden'}}>
        <div style={{
          color: highlight ? ACCENT : TEXT,
          fontFamily:'monospace', fontSize:12, fontWeight: highlight ? 800 : 600,
          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:280,
        }}>{value}</div>
        {copied
          ? <MdCheck size={13} color={GREEN}/>
          : <MdContentCopy size={12} color={SUBTLE}/>}
      </div>
    </div>
  );
}

// ???????????????????????????????????????????????????????????????????????????
// Timeline (vertical stepper from eventLog)
// ───────────────────────────────────────────────────────────────────────────
// BUGFIX (28-May-2026): previously this rendered raw eventLog entries (sorted
// by timestamp) and APPENDED unreached flow statuses at the end. That produced
// the "Refund requested → Credited → Refund initiated → Processing at bank"
// nonsense ordering when a historical/backfilled refund only had a single
// "credited" event in its log — the unreached "initiated" and "processed"
// steps were appended AFTER credited, displayed as pending future steps.
//
// New algorithm: always render the canonical Razorpay flow in fixed order
// (requested → initiated → processed → credited for refunds, created →
// authorized → captured for payments). A step is marked DONE if either:
//   (a) the eventLog explicitly contains an event with that status, OR
//   (b) a LATER step in the flow has been reached — implicit completion
//       (you can't credit money without having initiated and processed it).
// Timestamps come from explicit events; implicit-done steps show no time
// (they happened at some unrecorded moment before the next explicit event).
// ???????????????????????????????????????????????????????????????????????????
function Timeline({ txn }) {
  const isRefund = txn.type === 'refund';
  const flow = isRefund
    ? ['requested','initiated','processed','credited']
    : ['pending','success'];

  const events = [...(txn.eventLog || [])].sort((a,b)=>new Date(a.ts) - new Date(b.ts));

  // Index events by status — keep the EARLIEST event for each canonical
  // status so timestamps reflect when the state was first reached.
  const eventByStatus = {};
  events.forEach(e => { if (!eventByStatus[e.status]) eventByStatus[e.status] = e; });
  const failed = !!eventByStatus.failed;

  // Find the highest flow index that any event (explicit or implicit) has
  // reached. Anything <= that index is "done"; anything above is "pending".
  let highestDoneIdx = -1;
  flow.forEach((s, i) => { if (eventByStatus[s]) highestDoneIdx = Math.max(highestDoneIdx, i); });

  const steps = flow.map((s, i) => {
    const ev = eventByStatus[s];
    const done = !failed && i <= highestDoneIdx;
    const implicit = done && !ev;     // done by inference, no explicit event
    return {
      status:   s,
      title:    titleFor(s, isRefund),
      subtitle: ev?.note || (implicit ? '' : hintFor(s)),
      ts:       ev?.ts || null,
      by:       ev?.by || '',
      done, implicit, failure: false,
    };
  });

  // If the refund/payment failed, render a single "Failed" step at the
  // position it occurred so the admin sees the failure event in-line.
  if (failed) {
    const ev = eventByStatus.failed;
    steps.push({
      status:'failed', title:titleFor('failed', isRefund),
      subtitle: ev?.note || '', ts: ev?.ts, by: ev?.by || '',
      done:true, implicit:false, failure:true,
    });
  }

  if (steps.length === 0) {
    return <div style={{color:SUBTLE, fontSize:13}}>No timeline events yet.</div>;
  }

  return (
    <div>
      {steps.map((s, i) => {
        const isLast = i === steps.length - 1;
        const dotColor = s.failure ? RED : (s.done ? GREEN : '#1e293b');
        const lineColor = s.done ? `${GREEN}77` : BORDER;
        return (
          <div key={i} style={{display:'flex', gap:12, alignItems:'stretch'}}>
            <div style={{display:'flex', flexDirection:'column', alignItems:'center', minWidth:18}}>
              <div style={{
                width:16, height:16, borderRadius:'50%', background:dotColor,
                border:`2px solid ${BG}`, boxShadow:`0 0 0 1px ${dotColor}`,
                display:'grid', placeItems:'center', color:'#fff', fontSize:10,
              }}>
                {s.done ? (s.failure ? '×' : '✓') : null}
              </div>
              {!isLast && <div style={{flex:1, width:2, background:lineColor, marginTop:2}}/>}
            </div>
            <div style={{flex:1, paddingBottom:isLast ? 0 : 16}}>
              <div style={{fontWeight: s.done ? 800 : 600, color: s.done ? TEXT : SUBTLE, fontSize:13.5}}>
                {s.title}
              </div>
              {s.subtitle && <div style={{color:SUBTLE, fontSize:12, marginTop:2}}>{s.subtitle}</div>}
              {s.ts && (
                <div style={{color:SUBTLE, fontSize:11, marginTop:2}}>
                  {fmtShortDT(s.ts)}{s.by ? ` · ${s.by}` : ''}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function titleFor(status, isRefund) {
  if (isRefund) {
    return {
      requested:'Refund requested',
      initiated:'Refund initiated',
      processed:'Refund processing',
      credited:'Refund processed',          // matches Razorpay's wording
      failed:'Refund failed',
      note:'Note added',
    }[status] || status;
  }
  // Razorpay-style payment flow titles (Payment created → authorized → captured).
  return {
    pending:'Payment created',
    success:'Payment captured',
    failed:'Payment failed',
  }[status] || status;
}
function hintFor(status) {
  return {
    requested:'Waiting for admin approval',
    initiated:'Razorpay is sending to the bank',
    processed:'Takes 3-5 working days',
    credited:'Amount credited to customer\'s bank account',
    success:'Waiting for confirmation',
    pending:'Awaiting payment from customer',
  }[status] || '';
}

// ── Small styles ─────────────────────────────────────────────────────────────
const SELECT = {
  background:'#0f172a', border:`1px solid ${BORDER}`, color:TEXT, borderRadius:8,
  padding:'7px 10px', fontSize:13, fontWeight:700, fontFamily:'inherit', outline:'none',
};
const INPUT = {
  background:'#0f172a', border:`1px solid ${BORDER}`, color:TEXT, borderRadius:8,
  padding:'7px 10px', fontSize:13, fontWeight:700, fontFamily:'inherit', outline:'none',
};
const BTN = {
  background:ACCENT, border:`1px solid ${ACCENT}`, color:'#0b1220',
  padding:'7px 12px', borderRadius:8, cursor:'pointer', fontSize:12.5,
  fontWeight:800, fontFamily:'inherit', display:'inline-flex', alignItems:'center', gap:6,
};
const BTN_GHOST = {
  background:'transparent', border:`1px solid ${BORDER}`, color:TEXT,
  padding:'7px 12px', borderRadius:8, cursor:'pointer', fontSize:12.5,
  fontWeight:700, fontFamily:'inherit', display:'inline-flex', alignItems:'center', gap:6,
};

// ─────────────────────────────────────────────────────────────────────────────
// DateRangePill — Razorpay-style "Date Range: dd/mm/yyyy → dd/mm/yyyy" button
// that pops a small panel with two native date inputs and Apply / Clear.
// Closed state mirrors Razorpay's pill exactly so the visual hierarchy stays
// consistent with the rest of the filter bar.
// ─────────────────────────────────────────────────────────────────────────────
function DateRangePill({ from, to, onChange, open, setOpen }) {
  const fmtPart = (d) => {
    if (!d) return 'dd-mm-yyyy';
    const [y, m, da] = d.split('-');
    if (!y || !m || !da) return d;
    return `${da}-${m}-${y}`;
  };
  const hasRange = !!(from || to);
  return (
    <div style={{position:'relative'}}>
      <button
        type='button'
        onClick={() => setOpen(o => !o)}
        style={{
          ...BTN_GHOST,
          borderColor: hasRange ? ACCENT : BORDER,
          color: hasRange ? ACCENT : TEXT,
          background: hasRange ? `${ACCENT}11` : 'transparent',
          padding:'7px 12px',
        }}>
        <MdDateRange/> {hasRange
          ? <>Date: <strong style={{marginLeft:4}}>{fmtPart(from)}</strong> → <strong style={{marginLeft:4}}>{fmtPart(to)}</strong></>
          : 'Date range'}
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{
            position:'fixed', inset:0, zIndex:20,
          }}/>
          <div style={{
            position:'absolute', top:'calc(100% + 6px)', left:0, zIndex:21,
            background:CARD, border:`1px solid ${BORDER}`, borderRadius:12,
            padding:14, minWidth:300, boxShadow:'0 12px 40px rgba(0,0,0,0.45)',
          }}>
            <div style={{color:SUBTLE, fontSize:11, fontWeight:800, letterSpacing:1, marginBottom:8}}>
              SELECT DATE RANGE
            </div>
            <div style={{display:'flex', gap:8, alignItems:'center', marginBottom:10}}>
              <input type='date' value={from} onChange={e => onChange(e.target.value, to)} style={{...INPUT, flex:1}}/>
              <span style={{color:SUBTLE}}>→</span>
              <input type='date' value={to}   onChange={e => onChange(from, e.target.value)} style={{...INPUT, flex:1}}/>
            </div>
            {/* Quick presets */}
            <div style={{display:'flex', gap:6, flexWrap:'wrap', marginBottom:10}}>
              {[
                { l:'Today',         d:0 },
                { l:'Last 7 days',   d:7 },
                { l:'Last 30 days',  d:30 },
                { l:'Last 90 days',  d:90 },
              ].map(p => (
                <button key={p.l} type='button' onClick={() => {
                  const today = new Date();
                  const start = new Date(today.getTime() - p.d * 86400000);
                  const iso = (x) => x.toISOString().slice(0, 10);
                  onChange(iso(start), iso(today));
                }} style={{
                  ...BTN_GHOST, padding:'4px 8px', fontSize:11,
                }}>{p.l}</button>
              ))}
            </div>
            <div style={{display:'flex', gap:8, justifyContent:'flex-end'}}>
              <button type='button' onClick={() => { onChange('', ''); setOpen(false); }}
                style={{...BTN_GHOST, padding:'6px 10px', fontSize:11.5, color:RED, borderColor:`${RED}55`}}>
                Clear
              </button>
              <button type='button' onClick={() => setOpen(false)} style={{...BTN, padding:'6px 12px', fontSize:11.5}}>
                Apply
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
const TH = {
  textAlign:'left', padding:'12px 14px', color:MUTED, fontSize:11.5,
  fontWeight:900, letterSpacing:0.6, textTransform:'uppercase',
  borderBottom:`1px solid ${BORDER}`, whiteSpace:'nowrap',
};
const TD = {
  padding:'12px 14px', color:TEXT, fontSize:14, verticalAlign:'middle',
  whiteSpace:'nowrap',
};
